// Per-deal "last text" summaries for board cards (Finance → Supplements, Oct 16, 2026).
//
// Pure helpers: the board fetches the texts of the last 90 days for its deal ids,
// newest first (see fetchCommsMap.ts), and this reduces them to the newest row per
// deal plus a needs-reply flag. The flag is literal — the newest text on the deal
// came from the homeowner and nothing has gone out since — so a "Thanks!" counts
// until someone answers or an automated text goes out; Shannon judges from the
// preview. No supabase import here so the reducer stays unit-testable.

export interface CommsStripRow {
  deal_id: string | null;
  direction: string;
  body: string | null;
  created_at: string | null;
  sent_at: string | null;
}

export interface DealCommsSummary {
  /** who sent the newest text */
  direction: "inbound" | "outbound";
  body: string;
  /** when the newest text went out or came in (sent_at when known, else created_at) */
  at: string;
  /** the newest text is from the homeowner and nothing has gone out after it */
  needsReply: boolean;
}

/** deal id → summary. A deal with no text in the window has no entry. */
export type CommsMap = Record<string, DealCommsSummary>;

/** Rows must be ordered newest first; the first row seen per deal wins. */
export function reduceCommsMap(rows: CommsStripRow[]): CommsMap {
  const map: CommsMap = {};
  for (const r of rows) {
    if (!r.deal_id || map[r.deal_id]) continue;
    const direction = r.direction === "inbound" ? "inbound" : "outbound";
    map[r.deal_id] = {
      direction,
      body: r.body ?? "",
      at: r.sent_at ?? r.created_at ?? new Date(0).toISOString(),
      needsReply: direction === "inbound",
    };
  }
  return map;
}

/** The strip right after a send from the board, before the realtime refetch lands. */
export function withOptimisticOutbound(
  map: CommsMap,
  dealId: string,
  body: string,
  at: string = new Date().toISOString(),
): CommsMap {
  return { ...map, [dealId]: { direction: "outbound", body, at, needsReply: false } };
}

/** One line of a text: whitespace collapsed, cut at a word boundary with an ellipsis. */
export function previewText(body: string, max = 80): string {
  const flat = body.replace(/\s+/g, " ").trim();
  if (flat.length <= max) return flat;
  const cut = flat.slice(0, max);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace >= max - 15 ? cut.slice(0, lastSpace) : cut).replace(/[\s,;:.!?-]+$/, "") + "…";
}

/** "just now" · "5m ago" · "3h ago" · "2d ago" · "Oct 3" · "Oct 3, 2025". */
export function relTime(iso: string, now: number = Date.now()): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return "";
  const diff = Math.max(0, now - t);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const d = new Date(t);
  const sameYear = d.getFullYear() === new Date(now).getFullYear();
  return d.toLocaleDateString("en-US", sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" });
}
